import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api';


import invoiceIcon from '../assets/icons/invoice.svg';
import downloadIcon from '../assets/icons/download.svg';

/**
 * Mongoose-based "invoice" doc shape (possibly with populated fields):
 * {
 *   _id: string,
 *   description: string,
 *   amount: string,
 *   projectId: string | { _id: string, title: string, ... } | null,
 *   releaseId: string | { _id: string, title: string, coverArt: string, ... } | null,
 *   paymentStatus: 'paid' | 'needs_paid' | 'processed',
 *   fileName: string,
 *   uploadDate: string, // e.g. "2023-09-20" or ISO
 *   costType: string,   // "Recoupable", etc.
 *   ...
 * }
 *
 * "projects" (props) => array of project docs { _id: string, title: string, ... }
 * "releases" (props) => array of release docs { _id: string, title: string, coverArt: string, ... }
 */
const BACKEND_URL =
  process.env.REACT_APP_BACKEND_URL || 'https://release-management-backend.onrender.com';

const InvoiceDatabase = ({
  invoices = [],
  setInvoices, // if you need to update local state
  projects = [],
  releases = [],
  handleInvoiceStatusChange,
  disableClientPaging = true,

  // NEW: parent-controlled processed toggle (server-side)
  processed,                // boolean | undefined
  onToggleProcessed,        // function | undefined

  // (legacy fallback) if parent doesn't pass processed props,
  // we can still show a local toggle (off by default)
  hideProcessedToggle = true,
}) => {
  console.log('[InvoiceDatabase.js] invoices (props):', invoices);
  console.log('[InvoiceDatabase.js] projects (props):', projects);
  console.log('[InvoiceDatabase.js] releases (props):', releases);

  // --------------------------------------
  // 1) Filter / Search states
  // --------------------------------------
  const [searchTerm, setSearchTerm] = useState('');          // search by invoice.description

  // For Project typeahead
  const [projectSearch, setProjectSearch] = useState('');
  const [selectedProject, setSelectedProject] = useState(null);
  const [showProjectSuggestions, setShowProjectSuggestions] = useState(false);

  // For Release typeahead
  const [releaseSearch, setReleaseSearch] = useState('');
  const [selectedRelease, setSelectedRelease] = useState(null);
  const [showReleaseSuggestions, setShowReleaseSuggestions] = useState(false);

  // Page size for rendering rows
const PAGE_SIZE = 35;
const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
// Reset visible rows whenever filters change
const depProjectId = selectedProject ? String(selectedProject._id) : '';
const depReleaseId = selectedRelease ? String(selectedRelease._id) : '';

useEffect(() => {
  if (!disableClientPaging) {
    setVisibleCount(PAGE_SIZE);
  }
}, [disableClientPaging, processed, depProjectId, depReleaseId, projectSearch, releaseSearch, searchTerm]);
// Toggle: group rows by project
const [groupByProject, setGroupByProject] = useState(false);

  // --------------------------------------
  // 2) Helper: getProjectTitle
  //    Handle both string ID or an object
  // --------------------------------------
  const getProjectTitle = (projValue) => {
    if (!projValue) return 'No Project'; 

    // If invoice already has a populated doc => object with _id
    if (typeof projValue === 'object' && projValue._id) {
      return projValue.title || 'Unknown Project';
    }

    // Otherwise, treat it as a string ID
    const found = projects.find((p) => String(p._id) === String(projValue));
    return found ? found.title : 'No Project';
  };

  // --------------------------------------
  // 3) Helper: getReleaseDoc
  //    Handle both string ID or an object
  // --------------------------------------
  const getReleaseDoc = (relValue) => {
    if (!relValue) return null;

    // If it's already an object with _id => invoice has a populated release doc
    if (typeof relValue === 'object' && relValue._id) {
      return relValue;
    }

    // Otherwise, treat it as a string ID
    return releases.find((r) => String(r._id) === String(relValue)) || null;
  };

  // Helper: getReleaseIsrc
  const getReleaseIsrc = (relValue) => {
    if (!relValue) return '—'; // includes "General Costs" (null) and missing

    // populated release doc
    if (typeof relValue === 'object' && relValue._id) {
      return relValue.isrc || '—';
    }

    const doc = releases.find((r) => String(r._id) === String(relValue));
    return doc?.isrc || '—';
  };

  // Helper: getProjectDoc
// Returns the full project object (or null) whether the invoice stores an ID
// or an already-populated project document.
const getProjectDoc = (projValue) => {
  if (!projValue) return null;

  // If the invoice already has a populated project doc
  if (typeof projValue === 'object' && projValue._id) {
    return projValue;
  }

  // Otherwise look it up by ID
  return projects.find((p) => String(p._id) === String(projValue)) || null;
};

  // --------------------------------------
  // 4) Payment status => background color
  // --------------------------------------
  const getStatusBackground = (status) => {
    switch (status) {
      case 'paid':
        return '#28a745'; // green
      case 'needs_paid':
        return 'orange';
      case 'processed':
        return '#c376da'; // purple
      default:
        return '#555';    // grey fallback
    }
  };

  // --------------------------------------
  // 5) Typeahead suggestions (Project)
  // --------------------------------------
  const projectSuggestionsList = projects.filter((p) =>
    p.title.toLowerCase().includes(projectSearch.toLowerCase())
  );

  const clearProject = () => {
    setSelectedProject(null);
    setProjectSearch('');
    setShowProjectSuggestions(false);
  };

  // --------------------------------------
  // 6) Typeahead suggestions (Release)
  // --------------------------------------
  const releaseSuggestionsList = releases.filter((r) =>
    r.title.toLowerCase().includes(releaseSearch.toLowerCase())
  );

  const clearRelease = () => {
    setSelectedRelease(null);
    setReleaseSearch('');
    setShowReleaseSuggestions(false);
  };

  // --------------------------------------
  // 7) Filter logic
  // --------------------------------------
  // If parent hides the processed toggle, always show whatever parent sent.
// Otherwise, use the child's toggle state.
// If parent provides processed + onToggleProcessed, use parent state.
// Otherwise fall back to local toggle if not hidden.
const [localShowProcessed, setLocalShowProcessed] = useState(false);
const effectiveShowProcessed =
  typeof processed === 'boolean'
    ? processed
    : (hideProcessedToggle ? true : localShowProcessed);

let filteredInvoices = invoices.filter((inv) => {
  // Hide 'processed' if toggled off (child-level only when toggle is visible)
  if (!effectiveShowProcessed && inv.paymentStatus === 'processed') return false;

  // Project filter …
  if (selectedProject) {
    let invoiceProjId = inv.projectId;
    if (invoiceProjId && typeof invoiceProjId === 'object') {
      invoiceProjId = invoiceProjId._id || null;
    }
    if (String(invoiceProjId) !== String(selectedProject._id)) {
      return false;
    }
  }

  // Release filter …
  if (selectedRelease) {
    let invoiceRelId = inv.releaseId;
    if (invoiceRelId && typeof invoiceRelId === 'object') {
      invoiceRelId = invoiceRelId._id || null;
    }
    if (String(invoiceRelId) !== String(selectedRelease._id)) {
      return false;
    }
  }

  // Search filter …
  if (searchTerm && !inv.description?.toLowerCase().includes(searchTerm.toLowerCase())) {
    return false;
  }

  return true;
});

  // Sort newest first by uploadDate
  filteredInvoices = filteredInvoices.sort((a, b) => {
    const dateA = a.uploadDate ? new Date(a.uploadDate) : 0;
    const dateB = b.uploadDate ? new Date(b.uploadDate) : 0;
    return dateB - dateA;
  });

  console.log('[InvoiceDatabase.js] filteredInvoices =>', filteredInvoices);

  // Only render up to visibleCount rows for performance
const pagedInvoices = useMemo(() => {
  return disableClientPaging ? filteredInvoices : filteredInvoices.slice(0, visibleCount);
}, [disableClientPaging, filteredInvoices, visibleCount]);

// Group the paged rows by project title, and sort groups by the latest upload in that group (desc)
const groupedFromPaged = useMemo(() => {
  if (!groupByProject) return [];
  const map = new Map(); // key: project title -> { title, latest, items: [] }

  const titleOf = (projVal) => {
    // invoice.projectId may be an object (populated) or an id
    if (projVal && typeof projVal === 'object' && projVal.title) return projVal.title;
    const found = projects.find(p => String(p._id) === String(projVal));
    return found ? found.title : 'No Project';
  };

  for (const inv of pagedInvoices) {
    const t = titleOf(inv.projectId) || 'No Project';
    const bucket = map.get(t) || { title: t, latest: 0, items: [] };
    bucket.items.push(inv);
    const ts = inv.uploadDate ? new Date(inv.uploadDate).getTime() : 0;
    if (ts > bucket.latest) bucket.latest = ts;
    map.set(t, bucket);
  }

  const groups = Array.from(map.values());
  // sort by latest upload in the group (DESC)
  groups.sort((a, b) => b.latest - a.latest);
  return groups;
}, [groupByProject, pagedInvoices, projects]);

  // --------------------------------------
  // 8) "Download All" => single ZIP
  // --------------------------------------
  const handleDownloadAll = async () => {
    const fileNames = filteredInvoices.map((inv) => inv.fileName).filter(Boolean);
    if (fileNames.length === 0) {
      alert('No files to download for the current filters!');
      return;
    }
    console.log('[InvoiceDatabase.js] handleDownloadAll => fileNames:', fileNames);

    try {
      const response = await api('/api/invoiceupload/download-zip', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fileNames }),
      });
      if (!response.ok) {
        throw new Error('Failed to generate ZIP on server');
      }

      const blob = await response.blob();
      const zipUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = zipUrl;
      link.setAttribute('download', 'invoices.zip');
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(zipUrl);
    } catch (err) {
      console.error('[InvoiceDatabase.js] handleDownloadAll error:', err);
      alert('Error downloading ZIP: ' + err.message);
    }
  };

  // New: Download into folders by project
const handleDownloadGrouped = async () => {
  // Build payload: we need fileName + projectTitle per invoice
  const payload = filteredInvoices
    .filter(inv => inv.fileName)
    .map(inv => {
      // Get project title whether invoice.projectId is populated or an id
      let projTitle = '';
      if (inv.projectId && typeof inv.projectId === 'object' && inv.projectId.title) {
        projTitle = inv.projectId.title;
      } else {
        const match = projects.find(p => String(p._id) === String(inv.projectId));
        projTitle = match ? match.title : 'No Project';
      }
      return {
        fileName: inv.fileName,
        projectTitle: projTitle || 'No Project'
      };
    });

  if (payload.length === 0) {
    alert('No files to download for the current filters!');
    return;
  }

  try {
    const response = await api('/api/invoiceupload/download-zip-grouped', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ invoices: payload }),
    });
    if (!response.ok) throw new Error('Failed to generate grouped ZIP');

    const blob = await response.blob();
    const zipUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = zipUrl;
    link.setAttribute('download', 'invoices_grouped.zip');
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(zipUrl);
  } catch (err) {
    console.error('[InvoiceDatabase.js] handleDownloadGrouped error:', err);
    alert('Error downloading grouped ZIP: ' + err.message);
  }
};

// --------------------------------------
// 9) Download CSV (current filters)
// --------------------------------------
const csvEscape = (val) => {
  const s = val == null ? '' : String(val);
  // Escape " by doubling it, then wrap in quotes if needed
  const needsQuotes = /[",\n\r]/.test(s);
  const escaped = s.replace(/"/g, '""');
  return needsQuotes ? `"${escaped}"` : escaped;
};

const handleDownloadCSV = () => {
  // Build rows from CURRENTLY FILTERED invoices (not just visibleCount)
  const rows = filteredInvoices.map((inv) => {
    const releaseDoc = getReleaseDoc(inv.releaseId);
    const projectDoc = getProjectDoc(inv.projectId);

    const releaseTitle = releaseDoc?.title || projectDoc?.title || '';
    const releaseIsrc = getReleaseIsrc(inv.releaseId) === '—' ? '' : getReleaseIsrc(inv.releaseId);
    const projectTitle = getProjectTitle(inv.projectId) || '';

    return {
      release: releaseTitle,
      isrc: releaseIsrc,
      project: projectTitle,
      description: inv.description || '',
      amount_eur: inv.amount ?? '',
      cost_type: inv.costType || '',
      upload_date: inv.uploadDate ? String(inv.uploadDate).slice(0, 10) : '',
      status: inv.paymentStatus || '',
      file_name: inv.fileName || '',
      invoice_id: inv._id || '',
    };
  });

  const header = [
    'Release',
    'ISRC',
    'Project',
    'Description',
    'Amount (EUR)',
    'Cost Type',
    'Upload Date',
    'Status',
    'File Name',
    'Invoice ID',
  ];

  const lines = [
    header.map(csvEscape).join(','),
    ...rows.map((r) => ([
      r.release,
      r.isrc,
      r.project,
      r.description,
      r.amount_eur,
      r.cost_type,
      r.upload_date,
      r.status,
      r.file_name,
      r.invoice_id,
    ]).map(csvEscape).join(',')),
  ];

  const csv = '\uFEFF' + lines.join('\n'); // BOM helps Excel open UTF-8 correctly
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);

  const ts = new Date().toISOString().slice(0, 10);
  const a = document.createElement('a');
  a.href = url;
  a.download = `invoices_${ts}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

  // --------------------------------------
  // Render
  // --------------------------------------
  return (
    <div style={{ color: 'white', marginTop: '30px' }}>
      <h2 style={{ marginBottom: '20px' }}>Invoice Database</h2>

      {/* -------------- Top Filter Row -------------- */}
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '15px',
          marginBottom: '20px',
          alignItems: 'center',
        }}
      >
        

{/* Processed toggle — parent-controlled if provided, else local fallback */}
{typeof processed === 'boolean' && typeof onToggleProcessed === 'function' ? (
  <button
    onClick={onToggleProcessed}
    style={{
      padding: '10px 15px',
      backgroundColor: '#333',
      color: 'white',
      border: 'none',
      borderRadius: '5px',
      cursor: 'pointer',
    }}
  >
    {processed ? 'Hide Processed' : 'Show Processed'}
  </button>
) : (
  !hideProcessedToggle && (
    <button
      onClick={() => setLocalShowProcessed(v => !v)}
      style={{
        padding: '10px 15px',
        backgroundColor: '#333',
        color: 'white',
        border: 'none',
        borderRadius: '5px',
        cursor: 'pointer',
      }}
    >
      {localShowProcessed ? 'Hide Processed' : 'Show Processed'}
    </button>
  )
)}

        {/* Project Filter Typeahead */}
        <div style={{ position: 'relative' }}>
          <input
            type="text"
            placeholder="Filter by project..."
            value={selectedProject ? selectedProject.title : projectSearch}
            onChange={(e) => {
              setProjectSearch(e.target.value);
              setSelectedProject(null);
              setShowProjectSuggestions(true);
            }}
            onFocus={() => {
              if (!selectedProject && projectSearch.trim().length > 0) {
                setShowProjectSuggestions(true);
              }
            }}
            style={{
              padding: '10px',
              paddingRight: '25px',
              backgroundColor: '#333',
              color: 'white',
              border: 'none',
              borderRadius: '5px',
              cursor: 'text',
              minWidth: '180px',
            }}
          />
          {/* X to clear */}
          {(selectedProject || projectSearch) && (
            <span
              onClick={clearProject}
              style={{
                position: 'absolute',
                right: '8px',
                top: '50%',
                transform: 'translateY(-50%)',
                cursor: 'pointer',
                color: '#aaa',
                fontWeight: 'bold',
              }}
            >
              ×
            </span>
          )}

          {showProjectSuggestions && projectSearch.trim() && !selectedProject && (
            <div
              style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                width: '100%',
                backgroundColor: '#1e1e1e',
                borderRadius: '5px',
                border: '1px solid #555',
                zIndex: 10,
                maxHeight: '150px',
                overflowY: 'auto',
                marginTop: '5px',
              }}
            >
              {projectSuggestionsList.length > 0 ? (
                projectSuggestionsList.map((p) => (
                  <div
                    key={p._id}
                    style={{
                      padding: '10px',
                      cursor: 'pointer',
                      borderBottom: '1px solid #333',
                    }}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                      console.log('[InvoiceDatabase.js] Selected project =>', p);
                      setSelectedProject(p);
                      setProjectSearch('');
                      setShowProjectSuggestions(false);
                    }}
                  >
                    {p.title}
                  </div>
                ))
              ) : (
                <div style={{ padding: '10px', color: '#999' }}>
                  No matching projects
                </div>
              )}
            </div>
          )}
        </div>

        {/* Release Filter Typeahead */}
        <div style={{ position: 'relative' }}>
          <input
            type="text"
            placeholder="Filter by release..."
            value={selectedRelease ? selectedRelease.title : releaseSearch}
            onChange={(e) => {
              setReleaseSearch(e.target.value);
              setSelectedRelease(null);
              setShowReleaseSuggestions(true);
            }}
            onFocus={() => {
              if (!selectedRelease && releaseSearch.trim().length > 0) {
                setShowReleaseSuggestions(true);
              }
            }}
            style={{
              padding: '10px',
              paddingRight: '25px',
              backgroundColor: '#333',
              color: 'white',
              border: 'none',
              borderRadius: '5px',
              cursor: 'text',
              minWidth: '180px',
            }}
          />
          {/* X to clear */}
          {(selectedRelease || releaseSearch) && (
            <span
              onClick={clearRelease}
              style={{
                position: 'absolute',
                right: '8px',
                top: '50%',
                transform: 'translateY(-50%)',
                cursor: 'pointer',
                color: '#aaa',
                fontWeight: 'bold',
              }}
            >
              ×
            </span>
          )}

          {showReleaseSuggestions && releaseSearch.trim() && !selectedRelease && (
            <div
              style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                width: '100%',
                backgroundColor: '#1e1e1e',
                borderRadius: '5px',
                border: '1px solid #555',
                zIndex: 10,
                maxHeight: '150px',
                overflowY: 'auto',
                marginTop: '5px',
              }}
            >
              {releaseSuggestionsList.length > 0 ? (
                releaseSuggestionsList.map((r) => (
                  <div
                    key={r._id}
                    style={{
                      padding: '10px',
                      cursor: 'pointer',
                      borderBottom: '1px solid #333',
                    }}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                      console.log('[InvoiceDatabase.js] Selected release =>', r);
                      setSelectedRelease(r);
                      setReleaseSearch('');
                      setShowReleaseSuggestions(false);
                    }}
                  >
                    {r.title}
                  </div>
                ))
              ) : (
                <div style={{ padding: '10px', color: '#999' }}>
                  No matching releases
                </div>
              )}
            </div>
          )}
        </div>

        {/* Search by Invoice Description */}
        <input
          type="text"
          placeholder="Search description..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{
            padding: '10px',
            backgroundColor: '#333',
            color: 'white',
            border: 'none',
            borderRadius: '5px',
            minWidth: '200px',
          }}
        />
        {/* Grouping toggle */}
<button
  onClick={() => {
    setGroupByProject(v => !v);
    setVisibleCount(PAGE_SIZE); // reset page-size when toggling
  }}
  style={{
    padding: '10px 15px',
    backgroundColor: groupByProject ? '#28a745' : '#555',
    color: 'white',
    border: 'none',
    borderRadius: '5px',
    cursor: 'pointer',
  }}
>
  {groupByProject ? 'Ungroup by project' : 'Group by project'}
</button>

        {/* Download All button */}
        <button
          onClick={handleDownloadAll}
          style={{
            padding: '10px 15px',
            backgroundColor: '#555',
            color: 'white',
            border: 'none',
            borderRadius: '5px',
            cursor: 'pointer',
          }}
        >
          Download All
        </button>

        {/* Download grouped by project */}
<button
  onClick={handleDownloadGrouped}
  style={{
    padding: '10px 15px',
    backgroundColor: '#555',
    color: 'white',
    border: 'none',
    borderRadius: '5px',
    cursor: 'pointer',
  }}
>
  Download into folders
</button>

        {/* Download CSV */}
        <button
          onClick={handleDownloadCSV}
          style={{
            padding: '10px 15px',
            backgroundColor: '#555',
            color: 'white',
            border: 'none',
            borderRadius: '5px',
            cursor: 'pointer',
          }}
        >
          Download CSV
        </button>
      </div>



      {/* Invoice Table */}
      {invoices.length === 0 ? (
        <p style={{ color: '#aaa' }}>No invoices found.</p>
      ) : (
        <div
          style={{
            backgroundColor: '#1e1e1e',
            padding: '20px',
            borderRadius: '10px',
          }}
        >
          {filteredInvoices.length === 0 && (
            <p style={{ color: '#999' }}>No invoices match these filters.</p>
          )}

          {filteredInvoices.length > 0 && (
            <table
              style={{
                width: '100%',
                borderCollapse: 'collapse',
                tableLayout: 'fixed',
                color: 'white',
              }}
            >
              <thead>
                <tr style={{ backgroundColor: '#333' }}>
                  <th style={{ padding: '10px', textAlign: 'left', width: '16%' }}>
                    Release
                  </th>
                  <th style={{ padding: '10px', textAlign: 'left', width: '12%' }}>
                    ISRC
                  </th>
                  <th style={{ padding: '10px', textAlign: 'left', width: '16%' }}>
                    Project
                  </th>
                  <th style={{ padding: '10px', textAlign: 'left', width: '22%' }}>
                    Description
                  </th>
                  <th style={{ padding: '10px', textAlign: 'left', width: '10%' }}>
                    Amount (€)
                  </th>
                  <th style={{ padding: '10px', textAlign: 'left', width: '12%' }}>
                    Cost Type
                  </th>
                  <th style={{ padding: '10px', textAlign: 'left', width: '10%' }}>
                    Upload Date
                  </th>
                  <th style={{ padding: '10px', textAlign: 'left', width: '10%' }}>
                    Status
                  </th>
                  <th style={{ padding: '10px', textAlign: 'left', width: '10%' }}>
                    Download
                  </th>
                </tr>
              </thead>
              <tbody>
  {!groupByProject && pagedInvoices.map((inv) => {
    const invoiceId = inv._id;
    const releaseDoc = getReleaseDoc(inv.releaseId);
    const releaseIsrc = getReleaseIsrc(inv.releaseId);
    const projectTitle = getProjectTitle(inv.projectId);
    const projectDoc   = getProjectDoc(inv.projectId);

    return (
      <tr
        key={invoiceId}
        style={{
          borderBottom: '1px solid #555',
          backgroundColor: '#1e1e1e',
        }}
      >
        {/* Release (or Project if no release) */}
        <td style={{ padding:'10px', textAlign:'left' }}>
          {(releaseDoc?.coverArt || projectDoc?.coverArt) && (
            <img
              src={releaseDoc?.coverArt || projectDoc?.coverArt}
              alt="Art"
              style={{
                width:40, height:40, objectFit:'cover',
                borderRadius:4, marginRight:10, verticalAlign:'middle'
              }}
            />
          )}
          <span style={{ verticalAlign:'middle' }}>
            {releaseDoc?.title || projectDoc?.title || '—'}
          </span>
        </td>

        {/* ISRC */}
        <td style={{ padding: '10px', textAlign: 'left', color: '#bbb' }}>
          {releaseIsrc}
        </td>

        {/* Project */}
        <td style={{ padding: '10px', textAlign: 'left' }}>
          {projectTitle || 'No Project'}
        </td>

        {/* Description (link) */}
        <td style={{ padding: '10px', textAlign: 'left' }}>
          <Link
  to={`/invoice/${invoiceId}`}
  state={{ invoice: inv }}          
  style={{
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    textDecoration: 'none',
    color: 'inherit',
  }}
>
            <img
              src={invoiceIcon}
              alt="Invoice Icon"
              style={{ width: 20, height: 20, filter: 'brightness(0) invert(1)' }}
            />
            <strong>{inv.description}</strong>
          </Link>
        </td>

        {/* Amount */}
        <td style={{ padding: '10px', textAlign: 'left' }}>
          € {inv.amount}
        </td>

        {/* Cost */}
        <td style={{ padding: '10px', textAlign: 'left' }}>
          {inv.costType || '—'}
        </td>

        {/* Upload Date */}
        <td style={{ padding: '10px', textAlign: 'left', color: 'gray' }}>
          {inv.uploadDate ? inv.uploadDate.slice(0, 10) : ''}
        </td>

        {/* Status */}
        <td style={{ padding: '10px', textAlign: 'left' }}>
          <select
            value={inv.paymentStatus}
            onChange={(e) => handleInvoiceStatusChange(invoiceId, e.target.value)}
            style={{
              padding: '5px 10px',
              borderRadius: '5px',
              border: 'none',
              color: '#fff',
              cursor: 'pointer',
              backgroundColor: (inv.paymentStatus === 'paid'
                ? '#28a745'
                : inv.paymentStatus === 'needs_paid'
                  ? 'orange'
                  : '#c376da'),
            }}
          >
            <option value="paid">Paid</option>
            <option value="needs_paid">Unpaid</option>
            <option value="processed">Processed</option>
          </select>
        </td>

        {/* Download */}
        <td style={{ padding: '10px', textAlign: 'left' }}>
          {inv.fileName ? (
            <a
              href={`${BACKEND_URL}/uploads/${encodeURIComponent(inv.fileName)}`}
              download
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '5px',
                textDecoration: 'none',
                color: 'white',
                backgroundColor: '#444',
                padding: '5px 10px',
                borderRadius: '5px',
              }}
            >
              <img
                src={downloadIcon}
                alt="Download Invoice"
                style={{ width: 10, height: 10, filter: 'brightness(0) invert(1)' }}
              />
              Download
            </a>
          ) : (
            <span style={{ color: '#999' }}>No File</span>
          )}
        </td>
      </tr>
    );
  })}

  {groupByProject && groupedFromPaged.map(group => (
    <React.Fragment key={group.title}>
      {/* Group header row */}
      <tr>
        <td colSpan={9} style={{
          padding: '10px',
          background: '#222',
          fontWeight: 700,
          borderTop: '1px solid #444',
          borderBottom: '1px solid #444'
        }}>
          {group.title}
        </td>
      </tr>

      {/* Rows for this group */}
      {group.items.map((inv) => {
        const invoiceId = inv._id;
        const releaseDoc = getReleaseDoc(inv.releaseId);
        const releaseIsrc = getReleaseIsrc(inv.releaseId);
        const projectTitle = getProjectTitle(inv.projectId);
        const projectDoc   = getProjectDoc(inv.projectId);

        return (
          <tr
            key={invoiceId}
            style={{
              borderBottom: '1px solid #555',
              backgroundColor: '#1e1e1e',
            }}
          >
            {/* Release (or Project if no release) */}
            <td style={{ padding:'10px', textAlign:'left' }}>
              {(releaseDoc?.coverArt || projectDoc?.coverArt) && (
                <img
                  src={releaseDoc?.coverArt || projectDoc?.coverArt}
                  alt="Art"
                  style={{
                    width:40, height:40, objectFit:'cover',
                    borderRadius:4, marginRight:10, verticalAlign:'middle'
                  }}
                />
              )}
              <span style={{ verticalAlign:'middle' }}>
                {releaseDoc?.title || projectDoc?.title || '—'}
              </span>
            </td>

            {/* ISRC */}
            <td style={{ padding: '10px', textAlign: 'left', color: '#bbb' }}>
              {releaseIsrc}
            </td>

            {/* Project */}
            <td style={{ padding: '10px', textAlign: 'left' }}>
              {projectTitle || 'No Project'}
            </td>

            {/* Description (link) */}
            <td style={{ padding: '10px', textAlign: 'left' }}>
              <Link
  to={`/invoice/${invoiceId}`}
  state={{ invoice: inv }}
  style={{
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    textDecoration: 'none',
    color: 'inherit',
  }}
>
                <img
                  src={invoiceIcon}
                  alt="Invoice Icon"
                  style={{ width: 20, height: 20, filter: 'brightness(0) invert(1)' }}
                />
                <strong>{inv.description}</strong>
              </Link>
            </td>

            {/* Amount */}
            <td style={{ padding: '10px', textAlign: 'left' }}>
              € {inv.amount}
            </td>

            {/* Cost */}
            <td style={{ padding: '10px', textAlign: 'left' }}>
              {inv.costType || '—'}
            </td>

            {/* Upload Date */}
            <td style={{ padding: '10px', textAlign: 'left', color: 'gray' }}>
              {inv.uploadDate ? inv.uploadDate.slice(0, 10) : ''}
            </td>

            {/* Status */}
            <td style={{ padding: '10px', textAlign: 'left' }}>
              <select
                value={inv.paymentStatus}
                onChange={(e) => handleInvoiceStatusChange(invoiceId, e.target.value)}
                style={{
                  padding: '5px 10px',
                  borderRadius: '5px',
                  border: 'none',
                  color: '#fff',
                  cursor: 'pointer',
                  backgroundColor: (inv.paymentStatus === 'paid'
                    ? '#28a745'
                    : inv.paymentStatus === 'needs_paid'
                      ? 'orange'
                      : '#c376da'),
                }}
              >
                <option value="paid">Paid</option>
                <option value="needs_paid">Unpaid</option>
                <option value="processed">Processed</option>
              </select>
            </td>

            {/* Download */}
            <td style={{ padding: '10px', textAlign: 'left' }}>
              {inv.fileName ? (
                <a
                  href={`${BACKEND_URL}/uploads/${encodeURIComponent(inv.fileName)}`}
                  download
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '5px',
                    textDecoration: 'none',
                    color: 'white',
                    backgroundColor: '#444',
                    padding: '5px 10px',
                    borderRadius: '5px',
                  }}
                >
                  <img
                    src={downloadIcon}
                    alt="Download Invoice"
                    style={{ width: 10, height: 10, filter: 'brightness(0) invert(1)' }}
                  />
                  Download
                </a>
              ) : (
                <span style={{ color: '#999' }}>No File</span>
              )}
            </td>
          </tr>
        );
      })}
    </React.Fragment>
  ))}
</tbody>
            </table>
            )}
          {!disableClientPaging && filteredInvoices.length > 0 && visibleCount < filteredInvoices.length && (
  <div style={{ marginTop: 16, display: 'flex', justifyContent: 'center' }}>
    <button
      onClick={() => setVisibleCount(c => c + PAGE_SIZE)}
      style={{
        padding: '10px 20px',
        background: '#333',
        border: 'none',
        color: '#fff',
        borderRadius: 6,
        cursor: 'pointer',
      }}
    >
      Show more
    </button>
  </div>
)}
        </div>
      )}
    </div>
  );
};

export default InvoiceDatabase;